import { parametrosDoObjeto } from './parametros'
import { REFERENCIAS_ALTURA } from './referenciasAltura'
import type { ParteDoCorpo, ResultadoQuedaAnimada } from './quedaAnimada'
import type { CenaTabletop } from './tipos'

export type NivelQueda = 'baixo' | 'moderado' | 'grave' | 'critico'

export interface AvaliacaoQueda {
  readonly nivel: NivelQueda
  readonly titulo: string
  readonly detalhe: string
  /** Altura percorrida até parar ou bater (m). */
  readonly alturaM: number
  /** Velocidade no impacto com o terreno, em km/h; `null` se a queda terminou presa ao talabarte. */
  readonly velocidadeImpactoKmh: number | null
  /** Fator de queda (h/L, de 0 a 2); `null` sem talabarte. */
  readonly fatorDeQueda: number | null
  /** Pico de força no talabarte (kN); `null` sem talabarte. */
  readonly forcaPicoKN: number | null
  readonly preso: boolean
  readonly bateu: boolean
  /** Primeira parte do corpo que bateu; `null` se não houve impacto. */
  readonly parteAtingida: ParteDoCorpo | null
  /** Limite de força no corpo usado na avaliação (kN). */
  readonly limiteForcaKN: number
}

export interface OpcoesAvaliacaoQueda {
  /** Limite de força no corpo (kN); o padrão é a referência de 6 kN. */
  readonly limiteForcaKN?: number
}

export const NOME_DA_PARTE: Readonly<Record<ParteDoCorpo, string>> = { pes: 'os pés', corpo: 'o tronco', cabeca: 'a cabeça' }

export const LIMITE_FORCA_CORPO_PADRAO_KN = REFERENCIAS_ALTURA.forcaMaximaNoCorpoKN.valor

const ORDEM: readonly NivelQueda[] = ['baixo', 'moderado', 'grave', 'critico']
const maisGrave = (a: NivelQueda, b: NivelQueda) => (ORDEM.indexOf(a) >= ORDEM.indexOf(b) ? a : b)

/**
 * Traduz uma queda simulada em um nível de gravidade didático.
 *  - Sem talabarte (ou rompido): vale a altura da queda livre e a velocidade do impacto.
 *  - Preso: vale o pico de força no corpo (limite de 6 kN) e o fator de queda; bater no
 *    terreno antes do talabarte segurar agrava o nível.
 * O nível é uma leitura de ensino, não um prognóstico clínico.
 */
export function avaliarQueda(cena: CenaTabletop, resultado: ResultadoQuedaAnimada, opcoes: OpcoesAvaliacaoQueda = {}): AvaliacaoQueda | null {
  const limiteForcaKN = opcoes.limiteForcaKN && opcoes.limiteForcaKN > 0 ? opcoes.limiteForcaKN : LIMITE_FORCA_CORPO_PADRAO_KN
  if (resultado.situacao !== 'ok' || resultado.quadros.length === 0) return null
  const trabalhador = cena.objetos.find((o) => o.id === resultado.idTrabalhador)
  const rompeu = resultado.quadros.some((q) => q.fase === 'rompido')
  const preso = Boolean(resultado.idCorda) && !rompeu
  const bateu = resultado.impacto !== null

  const primeiro = resultado.quadros[0]!
  const ultimo = resultado.quadros[resultado.quadros.length - 1]!
  // do primeiro quadro ao último (o do impacto, quando há): quanto os pés desceram
  const alturaM = Math.max(0, primeiro.pes[1] - ultimo.pes[1])
  const velocidadeImpactoKmh = bateu ? resultado.impacto!.velocidadeMs * 3.6 : null

  const L = trabalhador ? parametrosDoObjeto(trabalhador).comprimentoTalabarteM : undefined
  const fatorDeQueda = preso && L ? Math.min(2, resultado.quedaLivreM / L) : null
  const forcaPicoKN = preso ? (resultado.simulado?.picoKN ?? resultado.choque?.forcaPicoKN ?? null) : null

  const alturaDaQueda = preso && !bateu ? resultado.quedaLivreM : alturaM
  let nivel: NivelQueda = alturaDaQueda > 6 ? 'critico' : alturaDaQueda > 3 ? 'grave' : alturaDaQueda > 1 ? 'moderado' : 'baixo'

  if (preso && !bateu) {
    // o talabarte segurou: o que importa é a força que o corpo recebeu
    const porForca: NivelQueda = forcaPicoKN === null || forcaPicoKN <= limiteForcaKN
      ? (fatorDeQueda !== null && fatorDeQueda > 1 ? 'moderado' : 'baixo')
      : forcaPicoKN <= limiteForcaKN * 1.5 ? 'grave' : 'critico'
    nivel = porForca
  } else if (preso && bateu) {
    nivel = maisGrave(nivel, 'grave')
  }
  // onde bateu pesa: a cabeça sobe um nível (e nunca fica abaixo de moderado); cair de pés absorve melhor
  const parteAtingida = resultado.impacto?.parteDoCorpo ?? null
  if (parteAtingida === 'cabeca') nivel = maisGrave(ORDEM[Math.min(ORDEM.length - 1, ORDEM.indexOf(nivel) + 1)]!, 'moderado')
  if (rompeu) nivel = maisGrave(nivel, 'grave')

  const metros = (v: number) => `${v.toFixed(1).replace('.', ',')} m`
  const kmh = velocidadeImpactoKmh === null ? '' : ` a ${Math.round(velocidadeImpactoKmh)} km/h`
  let titulo: string
  let detalhe: string
  if (rompeu) {
    titulo = 'Talabarte rompeu'
    detalhe = `O talabarte não suportou o choque: o trabalhador seguiu em queda livre${bateu ? ` e bateu em ${resultado.impacto!.alvo}${kmh}` : ''}.`
  } else if (preso && !bateu) {
    titulo = 'Queda retida pelo talabarte'
    detalhe = `Queda livre de ${metros(resultado.quedaLivreM)}, pico de ${forcaPicoKN === null ? '—' : forcaPicoKN.toFixed(1).replace('.', ',')} kN no corpo (limite ${limiteForcaKN} kN).`
  } else if (preso) {
    titulo = 'Talabarte curto demais para a altura'
    detalhe = `Bateu em ${resultado.impacto!.alvo}${kmh} antes de o talabarte parar a queda (${metros(alturaM)} de altura).`
  } else {
    titulo = 'Queda livre sem proteção'
    detalhe = `Caiu ${metros(alturaM)}${bateu ? ` e atingiu ${resultado.impacto!.alvo}${kmh}, primeiro com ${NOME_DA_PARTE[parteAtingida!]}` : ''}.`
  }

  return { nivel, titulo, detalhe, alturaM, velocidadeImpactoKmh, fatorDeQueda, forcaPicoKN, preso, bateu, parteAtingida, limiteForcaKN }
}
